import { CoreError } from '../errors/core';
import { MusicTheoryService, TuningDefinition } from '../music-theory-service';
import { FretPosition } from './types';

export function computeFretPositions(args: {
  numStrings: number;
  numFrets: number;
  tuning: TuningDefinition;
  instrumentName: string;
}): Map<string, FretPosition> {
  const { numStrings, numFrets, tuning, instrumentName } = args;
  const positions = new Map<string, FretPosition>();

  const chromaticScale: readonly string[] = [
    'C',
    'C#',
    'D',
    'D#',
    'E',
    'F',
    'F#',
    'G',
    'G#',
    'A',
    'A#',
    'B',
  ];

  const getKey = (stringIndex: number, fret: number) => `${stringIndex}-${fret}`;

  for (let string = 0; string < numStrings; string++) {
    const openPitch = tuning.pitches[string];
    if (!openPitch) {
      throw new CoreError(
        `Invalid tuning: missing pitch for string index ${string}`,
        'INVALID_TUNING_PITCH_MISSING',
        { stringIndex: string, tuningId: tuning.id, instrument: instrumentName }
      );
    }
    const openNoteName = openPitch.note;
    const openNoteOctave = openPitch.octave;

    const noteIndex = chromaticScale.findIndex(
      (n) => n === openNoteName || MusicTheoryService.areNotesEnharmonic(n, openNoteName)
    );
    if (noteIndex === -1) {
      throw new CoreError(
        `Could not find note ${openNoteName} in chromatic scale`,
        'NOTE_LOOKUP_FAILED',
        { noteName: openNoteName }
      );
    }

    for (let fret = 0; fret <= numFrets; fret++) {
      let noteName: string;
      let noteOctave: number;
      if (fret === 0) {
        noteName = openNoteName;
        noteOctave = openNoteOctave;
      } else {
        const newNoteIndex = (noteIndex + fret) % 12;
        const nextNote = chromaticScale[newNoteIndex];
        if (!nextNote) {
          throw new CoreError(
            `Chromatic scale lookup failed for index ${newNoteIndex}`,
            'NOTE_LOOKUP_FAILED',
            { newNoteIndex }
          );
        }
        noteName = nextNote;
        const octaveIncrease = Math.floor((noteIndex + fret) / 12);
        noteOctave = openNoteOctave + octaveIncrease;
      }

      positions.set(getKey(string, fret), {
        string,
        fret,
        pitch: { note: noteName, octave: noteOctave },
      });
    }
  }

  return positions;
}
